import { useEffect } from 'react';
import confetti from 'canvas-confetti';
import { useLocalStorage } from 'Utilities/UseStorage';

interface IConfettiProps {
  id?: string;
  confettiFireLimit?: number;
  colors?: string;
}

const confettiDefaultKey = 'CONFETTI_PLACEHOLDER';

const getNumberOfTimesFired = (id: string, firingLimit: number): number => {
  if (id === confettiDefaultKey || firingLimit === -1) {
    return 0;
  }

  const storedData = localStorage.getItem(id);
  if (storedData) {
    try {
      const parsedData = JSON.parse(storedData);

      if (parsedData && parsedData.data && typeof parsedData.data === 'number') {
        return parsedData.data;
      }
    } catch (e) {
      return 0;
    }
  }
  return 0;
};

const Confetti = (props: IConfettiProps) => {
  const defaultColors = ['#FFD000', '#1A1446', '#06748C', '#28A3AF', '#C0BFC0'];
  const confettiID = props.id && props.id.length > 0 ? props.id : confettiDefaultKey;
  const colors = props.colors && props.colors.length > 0 ? props.colors.split(',') : defaultColors;
  const firingLimit = props.confettiFireLimit && props.confettiFireLimit > 0 ? props.confettiFireLimit : -1; // -1 stands for infinite firing

  // confettiStorage here is not assigned the value returned from the getNumberOfTimesFired() method
  // value assigned is undefined or 'data: null', unless confettiStorage has already been defined,
  // then the value returned will be the number of times it has actually fired.
  const [confettiStorage, setConfettiStorage] = useLocalStorage<number>(
    confettiID,
    getNumberOfTimesFired(confettiID, firingLimit),
  );

  useEffect(() => {
    localStorage.getItem(confettiDefaultKey) && localStorage.removeItem(confettiDefaultKey); // Clean up

    // Because confettiStorage is undefined or 'data: null' on creation, 0 has to be returned
    // This is because the setConfettiStorage will self correct the value and finally assign the correct value to the local storage variable
    // However when confettiStorage has a number assigned from previous firings, it will return a different value instead of the defualt 0
    // This is a very bizzare bug to say the least.
    let firingCount = confettiStorage ?? 0;

    if (firingLimit !== -1) {
      if (firingCount >= firingLimit) return;
      setConfettiStorage({ data: firingCount + 1 });
    } else {
      // Not counting firings for infinite mode, allows us to reset the firings count if it is changed in sitecore.
      setConfettiStorage({ data: 0 });
    }

    const fireConfetti = () => {
      const duration = 4 * 1000; // Duration in milliseconds
      const end = Date.now() + duration;

      (function frame() {
        // Calculate how much time is left
        const timeLeft = end - Date.now();
        const particleCount = 100 * (timeLeft / duration); // Particle count decreases with time

        // Set the confetti options
        confetti({
          particleCount: Math.random() * particleCount,
          angle: 90,
          spread: 90,
          origin: { x: Math.random(), y: Math.random() - 0.2 },
          colors: colors,
          gravity: 5,
          zIndex: 600,
        });

        // Stop if duration is over
        if (timeLeft > 0) {
          requestAnimationFrame(frame);
        }
      })();
    };

    setTimeout(() => {
      fireConfetti();
    }, 2000);

    // Intentionally run only once on mount; safe to ignore exhaustive-deps here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null; // This component doesn't need to render anything
};

export default Confetti;
